/**
 * ControlTestingSection (P279.3) -- the ToD/ToI/ToE control-testing panel
 * inside the assessment drawer. Each test carries a result pill + a manual
 * result select; Test of Design also runs the Engine over the P278-mapped
 * policy documents. The assessment status is the worst-of rollup
 * (backend-driven; the grid pill refreshes on save).
 */
import type { DocumentReadingCounts } from "shared/api/types";
import { DocumentReadingNote, sumReadings } from "shared/documents/DocumentReadingNote";
import { PassageExcerpt } from "shared/documents/PassageExcerpt";
import { InfoTip } from "shared/components/Explainer";
import { FirstMeetings } from "shared/glossary/FirstMeetings";
import { useUserPreferences } from "shared/api/userPreferences";
import { formatDateTime } from "shared/time/formatDate";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useControlTests,
  useAcceptTestDesign,
  useDeclineTestDesign,
  useTestDesign,
  useUpsertControlTest,
} from "framework/api/control_test_hooks";
import {
  useConfirmCandidate,
  useMapDocumentToControl,
  CANDIDATE_LIMIT,
  useControlCandidateDocuments,
} from "framework/api/useDocumentGap";
import { KpiPanel } from "framework/assessments/KpiPanel";
import { ProbePanel } from "framework/assessments/ProbePanel";
import { EngineBusy } from "shared/components/EngineBusy";
import { EngineOffBanner } from "shared/components/EngineOffBanner";
import { StatusPill, type PillTone } from "shared/components/StatusPill";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError } from "shared/api/client";
import { refusalMessage } from "shared/api/refusalMessage";
import { useToast } from "shared/toast/ToastProvider";
import { stateLabel } from "shared/vocab/workflowState";
import type {
  ControlTest,
  ControlTestResult,
  ControlTestType,
  TestDesignResult,
} from "shared/api/types";

import { Link } from "react-router-dom";
const TEST_TYPES: ControlTestType[] = ["design", "implementation", "effectiveness"];

const RESULTS: ControlTestResult[] = [
  "not_tested",
  "effective",
  "partially_effective",
  "ineffective",
];

const RESULT_TONE: Record<ControlTestResult, PillTone> = {
  effective: "green",
  partially_effective: "amber",
  ineffective: "red",
  not_tested: "neutral",
};

interface Props {
  cycleId: string;
  controlId: string;
}

export function ControlTestingSection({ cycleId, controlId }: Props): JSX.Element {
  const { t } = useTranslation();
  const tests = useControlTests(cycleId, controlId);
  // Round 17 A32 -- the rollup sentence taught on every drawer open.
  // It is shown until the person has met the term, then it is gone.
  const prefs = useUserPreferences();
  const metTesting = (prefs.data?.met_terms ?? []).includes("control_testing");
  const byType = new Map<ControlTestType, ControlTest>(
    (tests.data?.tests ?? []).map((x) => [x.test_type, x]),
  );

  return (
    <section data-testid="control-testing-section" className="space-y-2">
      <EngineOffBanner manualKey="engineOff.controlTesting" />
      <h3 className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-muted">
        {t("modules.framework.controlTesting.heading")}
        {/* F2: the appendix scored this section "yes" -- it is one of
            the few that already taught. The one thing it did not say
            is what the three tests each ASK. */}
        <InfoTip termKey="control_testing" />
      </h3>
      {prefs.data && !metTesting && (
        <p data-testid="control-testing-intro" className="text-xs text-ink-2">
          {t("modules.framework.controlTesting.intro")}
        </p>
      )}
      <FirstMeetings terms={["control_testing"]} />
      {/* ROUND 13 A5 -- withdrawing a test clears its result and its
          summary and leaves the assessment's status standing, because the
          rollup returns nothing for "not tested" and the caller's
          contract is to leave the status alone. The screen went from
          stating a position with a visible source to stating the same
          position with none. We do not invent the prior value; we name
          what is being left behind. */}
      {tests.data?.status_unsupported && (
        <p
          data-testid="control-test-status-unsupported"
          className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
        >
          {t("modules.framework.controlTesting.statusUnsupported", {
            status: stateLabel(tests.data.assessment_status ?? ""),
          })}
        </p>
      )}
      {/* ROUND 15 A18 -- "Withdrawing a test does not withdraw what it
          wrote" on a hand grade no test ever supported. A different
          fact, in its own words. */}
      {tests.data?.no_test_recorded && !tests.data.status_unsupported && (
        <p
          data-testid="control-test-none-recorded"
          className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-700"
        >
          {t("modules.framework.controlTesting.noTestRecordedYet", {
            status: stateLabel(tests.data.assessment_status ?? ""),
          })}
        </p>
      )}
      {TEST_TYPES.map((type) => (
        <TestRow
          key={type}
          cycleId={cycleId}
          controlId={controlId}
          testType={type}
          test={byType.get(type)}
        />
      ))}
      {/* P282: implementation probes feed Test of Implementation. */}
      <ProbePanel cycleId={cycleId} controlId={controlId} />
      {/* P280: effectiveness KPIs feed Test of Effectiveness. */}
      <KpiPanel cycleId={cycleId} controlId={controlId} />
    </section>
  );
}

function TestRow({
  cycleId,
  controlId,
  testType,
  test,
}: {
  cycleId: string;
  controlId: string;
  testType: ControlTestType;
  test: ControlTest | undefined;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const upsert = useUpsertControlTest(cycleId, controlId);
  const testDesign = useTestDesign(cycleId, controlId);
  const acceptToD = useAcceptTestDesign(cycleId, controlId);
  const declineToD = useDeclineTestDesign(cycleId, controlId);
  // Round 13 A2 -- the last run's PREVIEW (what accepting would do to the
  // status). Only the run response carries it.
  const [lastRun, setLastRun] = useState<TestDesignResult | null>(null);
  // Round 14 A1 -- the proposal itself lives on the ROW, not in this
  // component. Held in client state it vanished on reload, and a re-run
  // that would not change the status was never shown at all -- while
  // its words stood on the record. Now anything the Engine proposed is
  // on screen until a person accepts or declines it.
  const pending = test?.proposal ?? lastRun?.test.proposal ?? null;
  const blockedReason = lastRun?.blocked_reason ?? null;
  // Round 14 A2 -- the document tried to direct its reader, by forging
  // the fence or in plain words. Either way the person sees it BEFORE
  // deciding, with the excerpt already open.
  const directives = pending?.directive_passages ?? [];
  const addressedEngine =
    directives.length > 0 || (pending?.boundary_markers_stripped ?? 0) > 0;
  const [mapping, setMapping] = useState(false);
  // E1 -- why Test of Design cannot run yet, kept on screen.
  const [blocked, setBlocked] = useState<"no_documents" | null>(null);
  // ROUND 15 A10 -- the Engine's refusal, ON THE CONTROL. Four presses
  // in the adversarial probe each produced a toast and left the control
  // saying nothing; the sentence stays here until the next run.
  const [refused, setRefused] = useState<string | null>(null);
  const [result, setResult] = useState<ControlTestResult>(test?.result ?? "not_tested");

  const currentResult = test?.result ?? "not_tested";

  const save = async () => {
    await upsert.mutateAsync({ testType, body: { result } });
    toast.push({
      tone: "success",
      message: t("modules.framework.controlTesting.saved"),
    });
  };

  const runToD = async () => {
    setRefused(null);
    try {
      const res = await testDesign.mutateAsync();
      setBlocked(null);
      // NOT setResult(...): pre-filling the manual select with the
      // Engine's answer would let an ordinary Save write it under the
      // tester's name.
      setLastRun(res);
      toast.push({
        tone: "success",
        message: t("modules.framework.controlTesting.todDone", {
          count: res.covering_documents.length,
        }),
      });
    } catch (e) {
      const err = e as { status?: number };
      // E1 (round 8) -- a prerequisite belongs on the drawer, not in a
      // toast.
      //
      // The 422 says a document must be mapped before this can run,
      // which is a thing the operator has to go and DO. A toast says it
      // once and fades, so the sentence he needs while he works was the
      // one sentence that disappeared -- and he saw it four times on
      // the way out: twice from here, twice from the global failure
      // sink, in two wordings, because the server and the catalogue
      // phrase it differently. It stays on screen now, beside the verb
      // that satisfies it, and only the causes he can do nothing about
      // still raise a toast.
      if (err.status === 422) {
        setBlocked("no_documents");
        // Round 9 B6: the sentence stays on the drawer because it names
        // work to do; the toast exists because she may have looked away.
        // One, not four -- the reason lives on the control, so the toast
        // only has to say that nothing happened and where to look.
        toast.push({
          tone: "warning",
          message: t("modules.framework.controlTesting.todDidNotRun"),
        });
        return;
      }
      // A10 -- a refusal is a sentence on the control (and one toast
      // saying that nothing happened). Never the exception's text.
      const sentence =
        err.status === 503
          ? t("modules.framework.controlTesting.engineOff")
          : e instanceof ApiError && e.code
            ? refusalMessage(t, e)
            : t("modules.framework.controlTesting.todError");
      setRefused(sentence);
      toast.push({ tone: "warning", message: sentence });
    }
  };

  return (
    <div
      data-testid={`control-test-${testType}`}
      className="rounded-md border border-paper-2 p-2 text-sm"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium text-ink">
          {t(`modules.framework.controlTesting.type.${testType}`)}
        </span>
        <StatusPill tone={RESULT_TONE[currentResult]}>
          {t(`modules.framework.controlTesting.result.${currentResult}`)}
        </StatusPill>
      </div>
      {test?.summary && <p className="mt-1 text-xs text-ink-2">{test.summary}</p>}
      {/* ROUND 14 A3 -- a rejected mapping left the test that read it
          standing with the status it produced, and nothing said so. The
          result stays (a person accepted it); the record says what it
          rested on no longer covers the control. */}
      {(test?.withdrawn_documents?.length ?? 0) > 0 && (
        <p
          data-testid={`control-test-withdrawn-${testType}`}
          className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
        >
          {t("modules.framework.controlTesting.resultRestsOnWithdrawn", {
            count: test?.withdrawn_documents?.length ?? 0,
            names: (test?.withdrawn_documents ?? []).join(", "),
          })}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select
          data-testid={`control-test-select-${testType}`}
          value={result}
          onChange={(e) => setResult(e.target.value as ControlTestResult)}
          className="rounded border border-paper-2 bg-paper px-2 py-1 text-xs"
        >
          {RESULTS.map((r) => (
            <option key={r} value={r}>
              {t(`modules.framework.controlTesting.result.${r}`)}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={save}
          disabled={upsert.isPending}
          data-testid={`control-test-save-${testType}`}
          className="rounded bg-brand-brass px-2 py-1 text-xs text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
        >
          {t("modules.framework.controlTesting.save")}
        </button>
        {testType === "design" && (
          <>
            {refused && (
              <p
                data-testid="control-test-tod-refused"
                className="mb-1.5 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-900"
              >
                {refused}
                {/* A17 -- a refused test still read nothing, but the
                    documents it would have read are the same documents;
                    what they contain is worth saying here too. */}
                <RefusedReadingNote controlId={controlId} />
              </p>
            )}
            <button
              type="button"
              onClick={runToD}
              disabled={testDesign.isPending}
              data-testid="control-test-engine-tod"
              className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-1 text-xs text-brand-brassDark disabled:opacity-50"
            >
              {testDesign.isPending ? (
                <EngineBusy label={t("modules.framework.controlTesting.todBusy")} />
              ) : (
                t("modules.framework.controlTesting.runToD")
              )}
            </button>
          </>
        )}
        {testType === "design" && (
          <button
            type="button"
            data-testid="control-test-map-toggle"
            onClick={() => setMapping((v) => !v)}
            className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:border-brand-brass"
          >
            {t("modules.framework.controlTesting.mapDocument")}
          </button>
        )}
      </div>
      {/* Round 19 A44 -- a declined proposal left no line on a drawer
          with no recorded test: the reviewer could not tell that the
          Engine had proposed and a person had turned it down. Shown
          wherever a proposal was declined, tested or not. */}
      {testType === "design" && (test?.declines?.length ?? 0) > 0 && (
        <ul
          data-testid="control-test-tod-declines"
          className="space-y-0.5 text-[11px] text-slate-600"
        >
          {(test?.declines ?? []).map((d, i) => {
            const k = "modules.framework.controlTesting";
            const who = d.declined_by_name ?? t(`${k}.recordSomeone`);
            const when = formatDateTime(d.declined_at, i18n.language);
            return (
              <li key={i}>
                {d.declined_result
                  ? t(`${k}.recordDeclined`, {
                      who,
                      when,
                      result: t(`${k}.result.${d.declined_result}`),
                    })
                  : t(`${k}.recordDeclinedNoResult`, { who, when })}
              </li>
            );
          })}
        </ul>
      )}
      {testType === "design" && pending && !blockedReason && (
        <div
          data-testid="control-test-tod-proposal"
          className="rounded border border-brand-brass/40 bg-brand-brass/5 px-2 py-2 text-[11px] text-slate-700"
        >
          <p className="font-medium text-brand-brassDark">
            {t("modules.framework.controlTesting.todProposesTitle")}
          </p>
          <p className="mt-0.5" data-testid="control-test-tod-proposed-result">
            {t("modules.framework.controlTesting.todProposedResult", {
              result: t(`modules.framework.controlTesting.result.${pending.result}`),
            })}
          </p>
          {/* Round 18 A35 -- the direction sits beside the result it may
              have produced, not under the fold below the excerpt. */}
          {directives.length > 0 && (
            <div
              data-testid="control-test-tod-directive-warning"
              className="mt-1.5 rounded border border-rag-red/40 bg-rag-red/5 px-2 py-1 text-[11px] text-rag-red"
            >
              <p className="font-medium">
                {t("modules.framework.controlTesting.todDirectiveHeading", {
                  count: directives.length,
                })}
              </p>
              <ul className="mt-1 space-y-1">
                {directives.map((d, i) => (
                  <li key={i}>
                    <span className="text-slate-600" dir="auto">
                      {d.document}:{" "}
                    </span>
                    <q className="text-rag-red" dir="auto">
                      {d.passage}
                    </q>
                    {/* Round 16 A26 -- which one the original hid. */}
                    {d.hidden && (
                      <span
                        data-testid="control-test-tod-directive-hidden"
                        className="ms-1 text-[10px] font-semibold text-amber-900"
                      >
                        ({t("documents.reading.notVisible")})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {pending.suggested_ml != null && (
            <p
              className="mt-0.5 text-slate-600"
              data-testid="control-test-tod-suggested-ml"
            >
              {t("modules.framework.controlTesting.todSuggestedMl", {
                ml: pending.suggested_ml,
              })}
            </p>
          )}
          {pending.summary && (
            <p className="mt-0.5 text-slate-600" dir="auto">
              {pending.summary}
            </p>
          )}
          {lastRun && (
            <p className="mt-0.5">
              {lastRun.would_create_assessment
                ? t("modules.framework.controlTesting.todWouldCreate", {
                    status: stateLabel(lastRun.rolled_up_status ?? ""),
                  })
                : lastRun.current_status === lastRun.rolled_up_status
                  ? t("modules.framework.controlTesting.todWouldKeep", {
                      status: stateLabel(lastRun.current_status ?? ""),
                    })
                  : t("modules.framework.controlTesting.todWouldChange", {
                      from: stateLabel(lastRun.current_status ?? ""),
                      to: stateLabel(lastRun.rolled_up_status ?? ""),
                    })}
            </p>
          )}
          <p className="mt-0.5 text-slate-500">
            {t("modules.framework.controlTesting.todNotYetRecorded")}
          </p>
          {/* Round 16 A24 -- the answer's own reasoning disagrees with it. */}
          {pending.reasoning_disagrees && (
            <p
              data-testid="control-test-tod-reasoning-disagrees"
              className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
            >
              {t("modules.framework.controlTesting.todReasoningDisagrees")}
            </p>
          )}
          {(pending.withdrawn_documents?.length ?? 0) > 0 && (
            <p
              data-testid="control-test-tod-withdrawn"
              className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
            >
              {t("modules.framework.controlTesting.todReadWithdrawn", {
                count: pending.withdrawn_documents?.length ?? 0,
                names: (pending.withdrawn_documents ?? []).join(", "),
              })}
            </p>
          )}
          {/* Round 13 A3 -- what the judgement rested on, on the screen
              that writes the record. */}
          {pending.evidence_excerpt && (
            <details
              className="mt-1.5"
              data-testid="control-test-tod-excerpt"
              open={addressedEngine}
            >
              <summary className="cursor-pointer text-slate-600">
                {t("modules.framework.controlTesting.todExcerptSummary", {
                  count: pending.covering_documents?.length ?? 0,
                })}
              </summary>
              <PassageExcerpt
                text={pending.evidence_excerpt}
                passages={directives.map((d) => d.passage)}
                testId="control-test-tod-excerpt-text"
              />
            </details>
          )}
          {(pending.boundary_markers_stripped ?? 0) > 0 && (
            <p
              data-testid="control-test-tod-injection-warning"
              className="mt-1.5 rounded border border-rag-red/40 bg-rag-red/5 px-2 py-1 text-[11px] text-rag-red"
            >
              {t("modules.framework.controlTesting.todDocumentAddressedEngine", {
                count: pending.boundary_markers_stripped ?? 0,
              })}
            </p>
          )}
          <div className="mt-1.5 flex items-center gap-2">
            <button
              type="button"
              data-testid="control-test-tod-accept"
              disabled={acceptToD.isPending || declineToD.isPending}
              onClick={async () => {
                try {
                  await acceptToD.mutateAsync();
                } catch {
                  toast.push({
                    tone: "warning",
                    message: t("modules.framework.controlTesting.todActionFailed"),
                  });
                  return;
                }
                setLastRun(null);
                toast.push({
                  tone: "success",
                  message: t("modules.framework.controlTesting.todAccepted"),
                });
              }}
              className="rounded bg-brand-brass px-2 py-1 text-xs text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
            >
              {t("modules.framework.controlTesting.todAccept")}
            </button>
            <button
              type="button"
              data-testid="control-test-tod-discard"
              disabled={acceptToD.isPending || declineToD.isPending}
              onClick={async () => {
                try {
                  await declineToD.mutateAsync();
                } catch {
                  toast.push({
                    tone: "warning",
                    message: t("modules.framework.controlTesting.todActionFailed"),
                  });
                  return;
                }
                setLastRun(null);
                toast.push({
                  tone: "success",
                  message: t("modules.framework.controlTesting.todDeclined"),
                });
              }}
              className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:border-brand-brass disabled:opacity-50"
            >
              {t("modules.framework.controlTesting.todDiscard")}
            </button>
          </div>
        </div>
      )}
      {testType === "design" && blockedReason && (
        <p
          data-testid="control-test-tod-blocked"
          className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
        >
          {blockedReason}
        </p>
      )}
      {blocked === "no_documents" && (
        <p
          data-testid="control-test-blocked"
          className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
        >
          {t("modules.framework.controlTesting.todNoDocs")}
        </p>
      )}
      {/* B1 -- Test of Design has been a dead end for three rounds. It
          fails without a mapped policy document, and the only route to
          mapping one was a separate screen that offered no verb of its
          own. Naming a prerequisite is not the same as giving somebody
          a way to satisfy it, so the mapping happens here, where the
          person who hit the wall is standing.

          Candidates, not answers: a person still says that a document
          covers the control. Evidence a machine confirmed on a human's
          behalf is what the grounded-Engine posture exists to avoid. */}
      {mapping && testType === "design" && (
        <ControlDocumentPicker
          controlId={controlId}
          onMapped={() => setMapping(false)}
        />
      )}
    </div>
  );
}

function RefusedReadingNote({ controlId }: { controlId: string }) {
  const covering = useQuery<DocumentReadingCounts[]>({
    queryKey: ["control", controlId, "covering-documents"],
    queryFn: () =>
      api.get<DocumentReadingCounts[]>(
        `/v1/framework/controls/${controlId}/covering-documents`,
      ),
    retry: false,
  });
  const docs = Array.isArray(covering.data) ? covering.data : [];
  return (
    <DocumentReadingNote
      reading={sumReadings(docs)}
      subject="documents"
      testId="control-test-tod-refused-reading"
    />
  );
}

function ControlDocumentPicker({
  controlId,
  onMapped,
}: {
  controlId: string;
  onMapped: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [filter, setFilter] = useState("");
  const candidates = useControlCandidateDocuments(controlId, filter);
  const confirm = useConfirmCandidate();
  const mapDirect = useMapDocumentToControl();
  const docs = candidates.data ?? [];

  return (
    <div data-testid="control-document-picker" className="mt-2 space-y-1">
      {/* Round 17 A30 -- find a document by name, across the locker. */}
      <input
        type="search"
        dir="auto"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        data-testid="control-document-picker-filter"
        placeholder={t("modules.framework.controlTesting.filterPlaceholder")}
        className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
      />
      {/* D9 -- an empty picker was a dead end on the one step Test of
          Design cannot be done without. The catalogue names 209
          deliverables against six frameworks, so for most controls it
          cites none, and "no candidates" left her holding a test she
          could not run and no idea why. It says which of the two it is
          and where to go: the gap analysis is the surface that maps a
          document to a deliverable. */}
      {candidates.isSuccess && docs.length === 0 && (
        <p
          data-testid="control-document-picker-empty"
          className="text-[11px] text-ink-muted"
        >
          {t("modules.framework.controlTesting.noCandidates")}{" "}
          <Link
            to="/framework/document-gap"
            className="text-brand-brassDark hover:underline"
          >
            {t("modules.framework.controlTesting.noCandidatesRoute")}
          </Link>
        </p>
      )}
      {docs.length >= CANDIDATE_LIMIT && (
        <p
          data-testid="control-document-picker-more"
          className="text-[11px] text-ink-muted"
        >
          {t("modules.framework.controlTesting.moreOnGapPage", {
            count: docs.length,
          })}{" "}
          <Link
            to="/framework/document-gap"
            className="text-brand-brassDark hover:underline"
          >
            {t("modules.framework.controlTesting.gapPageLink")}
          </Link>
        </p>
      )}
      <ul className="space-y-0.5">
        {docs.map((d) => (
          <li
            key={`${d.document_id}:${d.deliverable_id}`}
            className="flex items-center gap-2 text-[12px]"
          >
            <span className="text-ink" dir="auto">
              {d.title ?? d.filename}
              <DocumentReadingNote
                reading={d}
                testId={`control-map-reading-${d.document_id}`}
              />
            </span>
            <span className="text-[10px] text-ink-muted" dir="auto">
              {/* B3 -- where the catalogue named no deliverable, say what
                  confirming will do rather than showing a blank column. */}
              {d.deliverable_name ??
                t("modules.framework.controlTesting.recordsDeliverable")}
            </span>
            <button
              type="button"
              data-testid={`control-map-${d.document_id}`}
              disabled={confirm.isPending || mapDirect.isPending}
              onClick={() => {
                const done = {
                  onSuccess: (res: unknown) => {
                    /* Round 19 A45 -- "Mapped." over a deliverable the
                       seed had already mapped, a press that wrote
                       nothing. The server says when the mapping stood. */
                    const r = (res ?? {}) as {
                      already_recorded?: boolean;
                      deliverable_name?: string | null;
                    };
                    toast.push(
                      r.already_recorded
                        ? {
                            tone: "info" as const,
                            message: t(
                              "modules.framework.controlTesting.alreadyCovered",
                              {
                                file: d.filename ?? d.title ?? "",
                                deliverable:
                                  r.deliverable_name ?? d.deliverable_name ?? "",
                              },
                            ),
                          }
                        : {
                            tone: "success" as const,
                            message: t("modules.framework.controlTesting.mapped"),
                          },
                    );
                    onMapped();
                  },
                  // Round 9 B6: no local failure toast. These two
                  // mutations are unmarked, so the global sink already
                  // announces a failure -- adding one here produced two
                  // toasts in two wordings for one 409 ("That did not
                  // save / Mapping already exists." and "409: Mapping
                  // already exists."). The sink's wording is the better
                  // of the two, and the sibling call on the gap page
                  // relies on it, so the duplicate goes rather than the
                  // shared one.
                };
                if (d.deliverable_id) {
                  confirm.mutate(
                    { documentId: d.document_id, deliverableId: d.deliverable_id },
                    done,
                  );
                } else {
                  // No catalogued deliverable for this control. Confirming
                  // mints one from the control itself, so the bank records
                  // what it expects instead of the picker staying empty.
                  mapDirect.mutate({ controlId, documentId: d.document_id }, done);
                }
              }}
              className="rounded border border-brand-brass/40 bg-brand-brass/5 px-1.5 py-0.5 text-[10px] font-medium text-brand-brassDark disabled:opacity-50"
            >
              {t("modules.framework.documentGap.confirmCovers")}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
